import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { ulid } from "ulid";
import type { CreateImportRequest, RecipeApiItem } from "@recipevault/shared";
import { ImportForm } from "../../features/imports/ImportForm.js";
import { submitImport } from "../../features/imports/api.js";
import { findDuplicateByUrl, normalizeRecipeUrl } from "../../features/imports/duplicates.js";
import { DuplicateNote } from "../../features/imports/DuplicateNote.js";
import { Button } from "../../ui/button.js";
import { ApiError } from "../../shared/http.js";

// Debounce the duplicate check so it doesn't run on every keystroke — only
// once the input settles, and only when it already parses as a URL.
const DUPLICATE_CHECK_DEBOUNCE_MS = 300;

export function NewImport(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const from = searchParams.get("from");
  const defaultKind: "url" | "text" = from === "text" ? "text" : "url";
  const prefillText = (location.state as { prefillText?: string } | null)?.prefillText;
  const [submitting, setSubmitting] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [duplicate, setDuplicate] = useState<RecipeApiItem | null>(null);

  useEffect(() => {
    const candidate = currentUrl.trim();
    if (normalizeRecipeUrl(candidate) === null) {
      setDuplicate(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void findDuplicateByUrl(candidate).then((dup) => {
        if (!cancelled) setDuplicate(dup);
      });
    }, DUPLICATE_CHECK_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentUrl]);

  async function onSubmit(req: CreateImportRequest): Promise<void> {
    setSubmitting(true);
    try {
      const { jobId } = await submitImport(req, ulid());
      await navigate(`/imports/${encodeURIComponent(jobId)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error("Monthly cap reached. See Settings → Household.");
      } else {
        toast.error(err instanceof Error ? err.message : "Import failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-6 space-y-6">
      <h1 className="text-2xl font-semibold">Import a recipe</h1>
      <p className="text-sm text-muted-foreground">
        Paste a URL or recipe text. We'll extract ingredients, steps, and
        metadata using AI.
      </p>
      {duplicate && (
        <div className="space-y-3">
          <DuplicateNote title={duplicate.title} />
          <Button asChild variant="outline" className="min-h-12 w-full">
            <Link to={`/recipes/${duplicate.id}`}>Open recipe</Link>
          </Button>
        </div>
      )}
      <ImportForm
        submitting={submitting}
        defaultKind={defaultKind}
        defaultText={prefillText}
        onUrlChange={setCurrentUrl}
        onKindChange={(kind) => {
          if (kind !== "url") {
            setDuplicate(null);
            setCurrentUrl("");
          }
        }}
        submitLabel={duplicate ? "Import anyway" : "Import recipe"}
        onSubmit={(req) => {
          void onSubmit(req);
        }}
      />
    </div>
  );
}
